import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { Label } from "../ui/label";
import { useTranslation } from "react-i18next";

/** Valid string for <input type="range">; empty/invalid/out-of-range → min (avoids browser midpoint thumb). */
function coerceToSliderString(raw, min, max) {
  if (raw === undefined || raw === null) return String(min);
  const str = String(raw).trim();
  if (str === "") return String(min);
  const n = Number(str);
  if (Number.isNaN(n)) return String(min);
  const clamped = Math.min(max, Math.max(min, n));
  return String(clamped);
}

const ActivitySlider = ({
  itemkey,
  content,
  onChange,
  defaultValue,
  value: valueProp = undefined,
  className,
  readOnly = false,
}) => {
  const min = Number(content.min ?? 0);
  const max = Number(content.max ?? 100);

  const isControlled = valueProp !== undefined && valueProp !== null;
  const [internalValue, setInternalValue] = useState(() =>
    coerceToSliderString(defaultValue, min, max),
  );
  const [thumbPosition, setThumbPosition] = useState(0);
  const sliderRef = useRef(null);
  const { i18n } = useTranslation();

  const value = isControlled
    ? coerceToSliderString(valueProp, min, max)
    : internalValue;

  useLayoutEffect(() => {
    if (!isControlled || readOnly || !onChange) return;
    const coerced = coerceToSliderString(valueProp, min, max);
    if (String(valueProp) !== coerced) {
      onChange(content.key, coerced);
    }
  }, [isControlled, readOnly, onChange, content.key, valueProp, min, max]);

  const direction = i18n.dir();

  const labels = (content.labels || "")
    .split(",")
    .map((l) => l.trim())
    .filter(Boolean);

  const updateThumbPosition = useCallback((sliderElement) => {
    if (!sliderElement) return;

    const currentValue = Number(sliderElement.value);
    const valuePercent = ((currentValue - min) / (max - min)) * 100;

    const rect = sliderElement.getBoundingClientRect();
    const trackWidth = rect.width;

    const thumbWidth = 32;

    let thumbCenter =
      thumbWidth / 2 + (valuePercent / 100) * (trackWidth - thumbWidth);

    let thumbCenterPercent = (thumbCenter / trackWidth) * 100;

    setThumbPosition(thumbCenterPercent);
  }, [min, max]);

  const handleSliderValueChange = (e) => {
    if (readOnly) return;
    const newVal = e.target.value;
    if (!isControlled) setInternalValue(newVal);
    if (onChange) onChange(content.key, newVal);
    updateThumbPosition(e.target);
  };

  useEffect(() => {
    if (isControlled) return;
    setInternalValue(coerceToSliderString(defaultValue, min, max));
  }, [isControlled, defaultValue, min, max]);

  useEffect(() => {
    if (sliderRef.current) {
      requestAnimationFrame(() => {
        updateThumbPosition(sliderRef.current);
      });
    }
  }, [value, updateThumbPosition]);

  useEffect(() => {
    if (sliderRef.current) {
      const updatePosition = () => {
        if (sliderRef.current) {
          updateThumbPosition(sliderRef.current);
        }
      };

      const timeoutId = setTimeout(updatePosition, 0);

      const handleResize = () => {
        updatePosition();
      };
      window.addEventListener("resize", handleResize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [updateThumbPosition]);

  const getPercent = (index, total) => {
    if (total < 2) return 0;
    return (100 * index) / (total - 1);
  };

  const labelData =
    labels.length < 2
      ? []
      : labels.map((lbl, i) => ({
          value: min + ((max - min) * i) / (labels.length - 1),
          label: lbl,
          percent: getPercent(i, labels.length),
        }));

  const currentValue = Number(value);
  const filledPercent = ((currentValue - min) / (max - min)) * 100;

  return (
    <div
      className={`mt-3 text-start p-4 bg-white border border-gray-200 rounded-xl shadow-[0_2px_8px_rgba(79,70,229,0.08)] overflow-visible${className ? ` ${className}` : ""}`}
      dir={direction}
    >
      <div className="flex justify-between items-center mb-3">
        <Label className="text-sm font-semibold text-gray-800">
          {content.label}
        </Label>
        <span className="text-sm font-bold text-primary tabular-nums">
          {/*{currentValue}*/}
        </span>
      </div>

      <div className="relative w-full px-1">
        <div
          className="absolute transition-all duration-150 ease-out pointer-events-none z-10 flex items-center justify-center"
          style={{
            left: `${thumbPosition}%`,
            transform: "translate(-50%, -50%)",
            top: "14px",
            width: "32px",
            height: "32px",
          }}
        >
          <span className="text-xs font-bold text-white tabular-nums drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]">
            {currentValue}
          </span>
        </div>
        <input
          ref={sliderRef}
          type="range"
          disabled={readOnly}
          className={`w-full h-2 rounded-full appearance-none cursor-pointer touch-pan-y [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_0_8px_rgba(79,70,229,0.12)] [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-150 hover:[&::-webkit-slider-thumb]:shadow-[0_0_0_10px_rgba(79,70,229,0.18)] [&::-moz-range-thumb]:w-8 [&::-moz-range-thumb]:h-8 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-[0_0_0_8px_rgba(79,70,229,0.12)] [&::-moz-range-thumb]:transition-all [&::-moz-range-thumb]:duration-150${readOnly ? " !cursor-default opacity-90 [&::-webkit-slider-thumb]:!cursor-default [&::-moz-range-thumb]:!cursor-default" : ""}`}
          style={{
            background: `linear-gradient(to right, hsl(var(--primary)) ${filledPercent}%, #E5E7EB ${filledPercent}%) `,
            ...(content.width ? { maxWidth: content.width } : {}),
          }}
          value={value}
          onChange={handleSliderValueChange}
          min={min}
          max={max}
          step={content.step}
          dir="ltr"
        />

        {labelData.length > 0 && (
          <div
            className="relative w-full px-2 mt-2 overflow-visible pointer-events-none select-none"
            style={{ minHeight: "1.5em" }}
            dir="ltr"
          >
            {labelData.map(({ percent, label }, i) => (
              <span
                key={i}
                className="text-gray-500 text-xs font-medium absolute"
                style={{
                  left:
                    i === 0
                      ? "0%"
                      : i === labelData.length - 1
                        ? "100%"
                        : `${percent}%`,
                  transform:
                    i === 0
                      ? "translateX(0)"
                      : i === labelData.length - 1
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivitySlider;
